import React, { useMemo, useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { extractApiError } from "@/lib/api";
import { ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip } from "recharts";
import { 
  Clock, MapPin, User, Car, Mail, Calendar, Truck, Smartphone, X, Loader2, AlertCircle, Route, Gauge, Battery, Activity, LocateFixed
} from "lucide-react";
import LocationLabel from "@/components/shared/LocationLabel";

const HISTORY_WINDOW_HOURS = 24;

function formatDateTime(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
}

function formatShortTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatCoordinate(value) {
  return Number.isFinite(Number(value)) ? Number(value).toFixed(5) : "N/A";
}

function formatLabel(value) {
  if (!value) return "N/A";
  return String(value).replace(/_/g, " ");
}

function formatDuration(seconds) {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "N/A";

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60);

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}

function formatLocationValue(value) {
  if (value === null || value === undefined || value === "") return "N/A";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map((item) => formatLocationValue(item)).filter((item) => item && item !== "N/A").join(", ") || "N/A";
  }
  if (typeof value === "object") {
    const parts = [
      value.name,
      value.address,
      value.place,
      value.location_name,
      value.display_name,
      value.suburb,
      value.city,
      value.district,
      value.state,
    ]
      .filter((part) => typeof part === "string" && part.trim())
      .map((part) => part.trim());

    if (parts.length > 0) {
      return parts.join(", ");
    }

    const lat = value.lat ?? value.latitude;
    const lon = value.lon ?? value.lng ?? value.longitude;
    if (lat !== undefined && lon !== undefined) {
      return `${lat}, ${lon}`;
    }
  }

  return "N/A";
}

function formatAddress(vehicle) {
  if (!vehicle) return "N/A";
  if (vehicle.address) return vehicle.address;
  const { district, city, state, pincode } = vehicle;
  const parts = [];
  if (district) parts.push(district);
  if (city && city !== district) parts.push(city);
  if (state && !parts.includes(state)) parts.push(state);
  let addr = parts.join(", ");
  if (pincode) addr = addr ? `${addr} - ${pincode}` : pincode;
  return addr || "N/A";
}

function extractCollection(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
}

function normalizeTelemetryPoint(item, index) {
  const timestamp = item?.recorded_at || item?.timestamp || item?.created_at || item?.dt || item?.event_timestamp || null;
  return {
    id: item?.id || `${timestamp || "point"}-${index}`,
    timestamp,
    speed: Number(item?.speed ?? item?.speed_kmph ?? item?.speed_km_h ?? 0),
    battery: Number(item?.battery ?? item?.battery_level ?? item?.battery_pct ?? item?.device_battery_pct ?? 0),
    device_battery_pct: item?.device_battery_pct ?? item?.device_battery ?? null,
    latitude: item?.latitude ?? item?.lat ?? item?.location?.lat ?? null,
    longitude: item?.longitude ?? item?.lng ?? item?.lon ?? item?.location?.lng ?? item?.location?.lon ?? null,
    heading: item?.heading ?? item?.bearing ?? null,
    ignition: item?.ignition ?? item?.ignition_on ?? null,
    rpm: item?.rpm ?? null,
    temperature: item?.temperature ?? null,
    cabin_temp: item?.cabin_temp ?? null,
    device_temp: item?.device_temp ?? null,
    passenger_count: item?.passenger_count ?? null,
    raw: item,
  };
}

function normalizeTrip(item, index) {
  const vehicleRegistration = item?.vehicle?.registration_number || item?.vehicle?.plate || item?.registration_number || item?.plate || "N/A";
  const distanceMeters = item?.distance_meters ?? item?.distanceMeters ?? null;
  return {
    id: item?.id || item?.trip_id || `trip-${index}`,
    status: item?.status || "unknown",
    startedAt: item?.start_dt || item?.started_at || item?.created_at || null,
    endedAt: item?.end_dt || item?.completed_at || item?.updated_at || null,
    vehicleRegistration,
    originLocation: item?.origin || item?.pickup_location || item?.start_location || item?.from_location || null,
    destinationLocation: item?.destination || item?.drop_location || item?.end_location || item?.to_location || null,
    distanceKm: item?.distance_km ?? item?.distance ?? (Number.isFinite(Number(distanceMeters)) ? Number(distanceMeters) / 1000 : null),
    durationSeconds: item?.duration_seconds ?? null,
    avgSpeedKmh: item?.summary?.avg_speed_kmh ?? item?.avg_speed_kmh ?? null,
    maxSpeedKmh: item?.summary?.max_speed_kmh ?? item?.max_speed_kmh ?? null,
    incidentId: item?.incident_id || item?.incident?.id || null,
  };
}

export default function VehicleProfileDialog({ open, onOpenChange, vehicleId }) {
  const [vehicle, setVehicle] = useState(null);
  const [latestTelemetry, setLatestTelemetry] = useState(null);
  const [telemetryHistory, setTelemetryHistory] = useState([]);
  const [recentTrips, setRecentTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sectionErrors, setSectionErrors] = useState({
    latestTelemetry: "",
    telemetryHistory: "",
    recentTrips: "",
  });

  useEffect(() => {
    if (!open || !vehicleId) {
      setVehicle(null);
      setLatestTelemetry(null);
      setTelemetryHistory([]);
      setRecentTrips([]);
      setSectionErrors({ latestTelemetry: "", telemetryHistory: "", recentTrips: "" });
      return;
    }

    const fetchVehicle = async () => {
      setLoading(true);
      setError("");
      setSectionErrors({ latestTelemetry: "", telemetryHistory: "", recentTrips: "" });
      try {
        const now = new Date();
        const fromDt = new Date(now.getTime() - HISTORY_WINDOW_HOURS * 60 * 60 * 1000).toISOString();
        const toDt = now.toISOString();

        const [vehicleResponse, latestResponse, historyResponse, tripsResponse] = await Promise.allSettled([
          api.get(`/vehicles/${vehicleId}`),
          api.get(`/vehicles/${vehicleId}/telemetry/latest`),
          api.get(`/vehicles/${vehicleId}/telemetry/history`, {
            params: {
              from_dt: fromDt,
              to_dt: toDt,
              page: 1,
              page_size: 500,
            },
          }),
          api.get("/trips", {
            params: {
              vehicle_id: vehicleId,
              page: 1,
              page_size: 20,
            },
          }),
        ]);

        if (vehicleResponse.status !== "fulfilled") {
          throw vehicleResponse.reason;
        }

        setVehicle(vehicleResponse.value?.data || null);

        if (latestResponse.status === "fulfilled") {
          setLatestTelemetry(latestResponse.value?.data || null);
        } else {
          setLatestTelemetry(null);
          setSectionErrors((prev) => ({
            ...prev,
            latestTelemetry: extractApiError(latestResponse.reason).message || "Failed to load latest telemetry.",
          }));
        }

        if (historyResponse.status === "fulfilled") {
          const historyRows = extractCollection(historyResponse.value?.data).map(normalizeTelemetryPoint);
          setTelemetryHistory(historyRows);
        } else {
          setTelemetryHistory([]);
          setSectionErrors((prev) => ({
            ...prev,
            telemetryHistory: extractApiError(historyResponse.reason).message || "Failed to load telemetry history.",
          }));
        }

        if (tripsResponse.status === "fulfilled") {
          const tripRows = extractCollection(tripsResponse.value?.data).map(normalizeTrip);
          setRecentTrips(tripRows);
        } else {
          setRecentTrips([]);
          setSectionErrors((prev) => ({
            ...prev,
            recentTrips: extractApiError(tripsResponse.reason).message || "Failed to load recent trips.",
          }));
        }
      } catch (err) {
        const { message } = extractApiError(err);
        setError(message);
        setVehicle(null);
        setLatestTelemetry(null);
        setTelemetryHistory([]);
        setRecentTrips([]);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicle();
  }, [open, vehicleId]);

  const chartData = useMemo(() => {
    return telemetryHistory
      .filter((point) => point.timestamp)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .map((point) => ({
        ...point,
        label: formatShortTime(point.timestamp),
      }));
  }, [telemetryHistory]);

  const latestKnownPoint = latestTelemetry || telemetryHistory[telemetryHistory.length - 1] || null;

  if (!vehicleId) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1400px] w-[95vw] p-0 rounded-[32px] overflow-hidden border-0 shadow-2xl bg-[#f1f5f9] h-[92vh] flex flex-col">
        {/* Header */}
        <div className="bg-white px-10 py-6 flex items-center justify-between shrink-0 border-b border-slate-100 relative z-10">
          <div className="flex items-center gap-6 flex-1">
            {loading ? (
              <div className="flex items-center justify-center w-full h-12">
                <Loader2 size={24} className="animate-spin text-blue-600" />
                <span className="ml-3 text-slate-600 font-medium">Loading vehicle details...</span>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center w-full h-12 text-rose-600 font-medium">
                {error}
              </div>
            ) : vehicle ? (
              <div className="flex flex-col gap-1 flex-1">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] font-black text-slate-400 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100 uppercase tracking-tighter">
                    {vehicle.vehicle_category || "VEHICLE"}
                  </span>
                  <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold ${vehicle.is_active ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${vehicle.is_active ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`}></span>
                    {vehicle.is_active ? "Active" : "Inactive"}
                  </span>
                  <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100">
                    <Clock size={12} className="text-blue-500" /> Device Linked
                  </span>
                </div>
                <h2 className="text-[36px] font-[900] text-[#0f172a] tracking-tight leading-none mt-2">{vehicle.registration_number}</h2>
                <div className="flex items-center gap-3 mt-2 text-[13px] font-bold text-slate-500">
                  <span className="bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{vehicle.vehicle_category}</span>
                  <span>{vehicle.state || "N/A"} / {vehicle.city || "N/A"} / {vehicle.district || "N/A"}</span>
                </div>
              </div>
            ) : null}
          </div>
          <button 
            onClick={() => onOpenChange(false)}
            className="w-12 h-12 rounded-2xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-all"
          >
            <X size={20} strokeWidth={3} />
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <Loader2 size={48} className="animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600 font-medium">Loading vehicle details...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <AlertCircle size={48} className="text-rose-500 mx-auto mb-4" />
              <p className="text-slate-900 font-bold">{error}</p>
            </div>
          </div>
        ) : vehicle ? (
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar bg-[#f1f5f9] p-10">
            <div className="grid min-h-full grid-cols-12 items-start gap-8 max-w-[1300px] mx-auto">
              {/* Left Column */}
              <div className="col-span-12 lg:col-span-8 space-y-8">
                {/* Driver Details */}
                <div className="bg-white rounded-[32px] p-8 shadow-sm border border-white/50">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-14 h-14 rounded-[22px] bg-[#0f172a] flex items-center justify-center text-white shadow-lg">
                      <User size={28} />
                    </div>
                    <div>
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Driver Details</p>
                      <h3 className="text-[24px] font-black text-slate-900 leading-tight">{vehicle.driver?.name || "N/A"}</h3>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-y-6 gap-x-8">
                    <DetailField label="Mobile" value={vehicle.driver?.phone || "N/A"} icon={Smartphone} />
                    <DetailField label="Email" value={vehicle.driver?.name ? `${vehicle.driver.name.toLowerCase().replace(/\s+/g, '.')}@example.com` : "N/A"} icon={Mail} />
                    <DetailField label="Source" value={vehicle.driver?.source || "N/A"} icon={User} className="col-span-2" />
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="bg-white rounded-[32px] p-8 shadow-sm border border-white/50">
                  <h3 className="text-[24px] font-black text-slate-900 mb-8">Vehicle Details</h3>
                  <div className="grid grid-cols-2 gap-y-6 gap-x-8">
                    <DetailField label="Registration" value={vehicle.registration_number} icon={Car} />
                    <DetailField label="VIN" value={vehicle.vin} icon={Truck} />
                    <DetailField label="Make" value={vehicle.make} icon={Car} />
                    <DetailField label="Model" value={vehicle.model} icon={Car} />
                    <DetailField label="Year" value={String(vehicle.year)} icon={Calendar} />
                    <DetailField label="Type" value={vehicle.vehicle_type} icon={Truck} />
                    <DetailField label="Address" value={formatAddress(vehicle)} icon={MapPin} className="col-span-2" />
                  </div>
                </div>

                <div className="bg-white rounded-[32px] p-8 shadow-sm border border-white/50 space-y-6">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Live Device Data</p>
                      <h3 className="text-[24px] font-black text-slate-900">Telemetry & trip history</h3>
                    </div>
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-[11px] font-bold text-slate-600">
                      Last {HISTORY_WINDOW_HOURS} hours
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                    <MetricCard
                      icon={Clock}
                      label="Last update"
                      value={formatDateTime(latestKnownPoint?.recorded_at || latestKnownPoint?.timestamp || latestTelemetry?.recorded_at)}
                    />
                    <MetricCard
                      icon={Gauge}
                      label="Speed"
                      value={`${Number.isFinite(Number(latestKnownPoint?.speed)) ? Number(latestKnownPoint.speed) : 0} km/h`}
                    />
                    <MetricCard
                      icon={Battery}
                      label="Battery"
                      value={`${Number.isFinite(Number(latestKnownPoint?.battery ?? latestTelemetry?.battery_level ?? latestTelemetry?.battery)) ? Number(latestKnownPoint?.battery ?? latestTelemetry?.battery_level ?? latestTelemetry?.battery) : 0}%`}
                    />
                    <MetricCard
                      icon={LocateFixed}
                      label="Coordinates"
                      value={(
                        <LocationLabel
                          locationValue={{
                            lat: latestKnownPoint?.latitude || latestTelemetry?.latitude,
                            lon: latestKnownPoint?.longitude || latestTelemetry?.longitude,
                          }}
                        />
                      )}
                    />
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <InfoTile label="RPM" value={latestKnownPoint?.rpm ?? latestTelemetry?.rpm ?? "N/A"} />
                    <InfoTile label="Engine Temp" value={latestKnownPoint?.temperature ?? latestTelemetry?.temperature ?? "N/A"} />
                    <InfoTile label="Cabin Temp" value={latestKnownPoint?.cabin_temp ?? latestTelemetry?.cabin_temp ?? "N/A"} />
                    <InfoTile label="Device Temp" value={latestKnownPoint?.device_temp ?? latestTelemetry?.device_temp ?? "N/A"} />
                    <InfoTile label="Device Battery %" value={
                      Number.isFinite(Number(latestKnownPoint?.device_battery_pct ?? latestTelemetry?.device_battery_pct ?? latestKnownPoint?.battery ?? latestTelemetry?.battery))
                        ? (latestKnownPoint?.device_battery_pct ?? latestTelemetry?.device_battery_pct ?? latestKnownPoint?.battery ?? latestTelemetry?.battery)
                        : "N/A"
                    } />
                    <InfoTile label="Passengers" value={latestKnownPoint?.passenger_count ?? latestTelemetry?.passenger_count ?? "N/A"} />
                  </div>

                  <Tabs defaultValue="telemetry" className="w-full">
                    <TabsList className="h-auto flex-wrap justify-start gap-2 rounded-2xl bg-slate-100 p-2">
                      <TabsTrigger value="telemetry" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Telemetry</TabsTrigger>
                      <TabsTrigger value="trips" className="rounded-xl px-4 py-2 data-[state=active]:shadow-sm">Trips</TabsTrigger>
                    </TabsList>

                    <TabsContent value="telemetry" className="mt-4 space-y-4">
                      {sectionErrors.telemetryHistory && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                          {sectionErrors.telemetryHistory}
                        </div>
                      )}

                      {chartData.length > 0 ? (
                        <div className="rounded-[28px] border border-slate-100 bg-slate-50 p-5">
                          <div className="mb-4 flex items-center justify-between gap-3">
                            <div>
                              <p className="text-sm font-bold text-slate-900">Telemetry trend</p>
                              <p className="text-xs text-slate-500">Speed and battery history for this vehicle.</p>
                            </div>
                            <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200">
                              {chartData.length} samples
                            </span>
                          </div>
                          <div className="h-[280px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="label" tickLine={false} axisLine={false} minTickGap={24} />
                                <YAxis yAxisId="speed" tickLine={false} axisLine={false} />
                                <YAxis yAxisId="battery" orientation="right" tickLine={false} axisLine={false} domain={[0, 100]} />
                                <Tooltip
                                  formatter={(value, name) => [name === "speed" ? `${value} km/h` : `${value}%`, name === "speed" ? "Speed" : "Battery"]}
                                  labelFormatter={(value, payload) => payload?.[0]?.payload?.timestamp ? formatDateTime(payload[0].payload.timestamp) : value}
                                />
                                <Line yAxisId="speed" type="monotone" dataKey="speed" stroke="#2563eb" strokeWidth={3} dot={false} />
                                <Line yAxisId="battery" type="monotone" dataKey="battery" stroke="#10b981" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      ) : (
                        <EmptyPanel title="No telemetry history" description="Telemetry history has not been recorded for the selected time window." />
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <InfoTile label="Ignition" value={latestKnownPoint?.ignition === null || latestKnownPoint?.ignition === undefined ? "N/A" : latestKnownPoint.ignition ? "On" : "Off"} />
                        <InfoTile label="Heading" value={latestKnownPoint?.heading ?? "N/A"} />
                      </div>
                    </TabsContent>

                    <TabsContent value="trips" className="mt-4 space-y-4">
                      {sectionErrors.recentTrips && (
                        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                          {sectionErrors.recentTrips}
                        </div>
                      )}

                      {recentTrips.length > 0 ? (
                        <div className="space-y-3">
                          {recentTrips.map((trip) => (
                            <div key={trip.id} className="rounded-[28px] border border-slate-100 bg-slate-50 p-5">
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <p className="text-sm font-bold text-slate-900">{trip.vehicleRegistration || "Vehicle trip"}</p>
                                  <p className="mt-1 text-xs text-slate-500">{formatDateTime(trip.startedAt)} → {formatDateTime(trip.endedAt)}</p>
                                </div>
                                <span className="rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-700 ring-1 ring-slate-200">
                                  {formatLabel(trip.status)}
                                </span>
                              </div>

                              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <InfoTile label="Origin" value={<LocationLabel locationValue={trip.originLocation} />} />
                                <InfoTile label="Destination" value={<LocationLabel locationValue={trip.destinationLocation} />} />
                                <InfoTile label="Distance" value={trip.distanceKm != null ? `${(Number(trip.distanceKm) / 1000).toFixed(2)} km` : "N/A"} />
                                <InfoTile label="Duration" value={formatDuration(trip.durationSeconds)} />
                                <InfoTile label="Average Speed" value={trip.avgSpeedKmh != null ? `${Number(trip.avgSpeedKmh).toFixed(1)} km/h` : "N/A"} />
                                <InfoTile label="Max Speed" value={trip.maxSpeedKmh != null ? `${Number(trip.maxSpeedKmh).toFixed(1)} km/h` : "N/A"} />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyPanel title="No recent trips" description="This vehicle does not have any trip records in the latest trip query." />
                      )}
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Location & Status */}
                <div className="bg-white rounded-[32px] p-8 shadow-sm border border-white/50">
                  <h3 className="text-[22px] font-black text-slate-900 mb-8">Location & Status</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">State</p>
                      <p className="text-[18px] font-black text-slate-900">{vehicle.state || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">City</p>
                      <p className="text-[18px] font-black text-slate-900">{vehicle.city || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">District</p>
                      <p className="text-[18px] font-black text-slate-900">{vehicle.district || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">Pincode</p>
                      <p className="text-[18px] font-black text-slate-900">{vehicle.pincode || "N/A"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">Status</p>
                      <p className="text-[18px] font-black text-slate-900 capitalize">{vehicle.status || "offline"}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">Incidents</p>
                      <p className="text-[18px] font-black text-rose-600">{vehicle.incident_count || 0}</p>
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 col-span-2">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">Location</p>
                      <LocationLabel
                        locationValue={{
                          lat: latestKnownPoint?.latitude || vehicle.latitude,
                          lon: latestKnownPoint?.longitude || vehicle.longitude,
                        }}
                        className="text-[18px] font-black text-slate-900"
                      />
                    </div>
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 col-span-2">
                      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-2">Last Trip</p>
                      <p className="text-[16px] font-bold text-slate-900">
                        {vehicle.last_trip ? new Date(vehicle.last_trip).toLocaleString() : "No trips recorded"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="col-span-12 lg:col-span-4 space-y-8">
                {/* Vehicle Card */}
                <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] rounded-[32px] overflow-hidden shadow-2xl relative h-[240px] group">
                  <div className="absolute top-0 right-0 w-full h-full opacity-30">
                    <img src="https://images.unsplash.com/photo-1621007947382-bb3c3994e3fb?q=80&w=1000&auto=format&fit=crop" alt="Vehicle" crossOrigin="anonymous" className="w-full h-full object-cover object-right" />
                    <div className="absolute inset-0 bg-gradient-to-r from-[#0f172a] via-[#0f172a]/60 to-transparent"></div>
                  </div>
                  <div className="p-8 relative z-10 h-full flex flex-col justify-between">
                    <div className="flex justify-between items-start">
                      <span className="bg-white/10 text-white/90 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest backdrop-blur-md">{vehicle.vehicle_category}</span>
                      <span className={`text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest backdrop-blur-md flex items-center gap-1.5 ${vehicle.status === 'offline' ? 'bg-slate-500/10 text-slate-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${vehicle.status === 'offline' ? 'bg-slate-400' : 'bg-emerald-400 animate-pulse'}`}></span> 
                        {vehicle.status === 'offline' ? 'Offline' : 'Online'}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <h2 className="text-white text-[38px] font-black leading-none">{vehicle.registration_number}</h2>
                      <p className="text-white/60 text-[15px] font-medium pt-1 uppercase">{vehicle.make} {vehicle.model}</p>
                    </div>
                  </div>
                </div>


                <div className="bg-white rounded-[32px] p-8 shadow-lg border border-gray-100">
  <h3 className="text-[16px] font-black text-black uppercase tracking-[3px] mb-6">
    IoT Details
  </h3>

  <div className="space-y-2">
    {(() => {
      const items = [
        { label: "Device ID", value: "" },
        { label: "MAC & Firmware", value: "" },
        { label: "Active", value: vehicle?.is_active ? "True" : "False" },
        { label: "Firmware Version", value: "" },
        { label: "Created", value: formatDateTime(vehicle?.created_at) },
        { label: "Vehicle Linked", value: vehicle?.registration_number ?? "N/A" },
        { label: "Status", value: vehicle?.status ?? "N/A" },
        { label: "Power Status", value: (latestKnownPoint?.device_battery_pct ?? latestTelemetry?.device_battery_pct ?? latestKnownPoint?.battery ?? latestTelemetry?.battery) != null ? `${latestKnownPoint?.device_battery_pct ?? latestTelemetry?.device_battery_pct ?? latestKnownPoint?.battery ?? latestTelemetry?.battery}%` : "N/A" },
        { label: "Connectivity", value: latestTelemetry ? "Connected" : "No recent signal" },
      ];

      return items.map((item, index) => (
        <div key={index} className="flex items-center justify-between rounded-lg bg-gray-50 border border-gray-100 px-3 py-3 hover:shadow-md transition-all">
          <span className="text-gray-500 text-sm">{item.label}</span>
          {item.label === "Power Status" ? (
            <div className="flex items-center gap-2">
              <Battery size={16} className="text-rose-500" />
              <span className="font-bold text-rose-600 text-sm">{item.value}</span>
            </div>
          ) : (
            <span className="font-bold text-gray-900 text-sm">{item.value}</span>
          )}
        </div>
      ));
    })()}
  </div>
</div>

                {/* Specs */}
                {/* <div className="bg-white rounded-[32px] p-8 shadow-sm border border-white/50">
                  <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest mb-6">Organization Info</p>
                  <div className="space-y-6">
                    <SidebarSpec label="Org Type" value={vehicle.org_type || "N/A"} />
                    <SidebarSpec label="Organization" value={vehicle.org_name || vehicle.organization?.name || "N/A"} />
                    <SidebarSpec label="Category" value={vehicle.vehicle_category || "N/A"} />
                    <SidebarSpec label="Vehicle Type" value={vehicle.vehicle_type || "N/A"} />
                    <SidebarSpec label="Telemetry feed" value={sectionErrors.latestTelemetry ? "Unavailable" : latestTelemetry ? "Connected" : "No recent signal"} />
                  </div>
                </div> */}

                {sectionErrors.latestTelemetry && (
                  <div className="rounded-[32px] border border-amber-200 bg-amber-50 p-6 text-sm text-amber-700">
                    {sectionErrors.latestTelemetry}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DetailField({ label, value, icon: Icon, className = "" }) {
  return (
    <div className={className}>
      <p className="text-[11px] font-black text-slate-400 uppercase tracking-widest leading-none mb-2 flex items-center gap-2">
        {Icon && <Icon size={14} className="text-blue-500" />}
        {label}
      </p>
      <p className="text-[15px] font-bold text-slate-900 break-all">{value}</p>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
      <p className="mb-2 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
        <Icon size={14} className="text-blue-500" />
        {label}
      </p>
      <p className="text-[15px] font-bold text-slate-900 break-words">{value || "N/A"}</p>
    </div>
  );
}

function InfoTile({ label, value }) {
  const displayValue = React.isValidElement(value) ? value : (value || "N/A");

  return (
    <div className="rounded-2xl border border-slate-100 bg-white px-4 py-3">
      <p className="text-[11px] font-black uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-bold text-slate-900 break-words">{displayValue}</p>
    </div>
  );
}

function EmptyPanel({ title, description }) {
  return (
    <div className="rounded-[28px] border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
      <Activity size={28} className="mx-auto mb-3 text-slate-300" />
      <p className="text-sm font-bold text-slate-900">{title}</p>
      <p className="mt-1 text-sm text-slate-500">{description}</p>
    </div>
  );
}

function SidebarSpec({ label, value }) {
  return (
    <div>
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-[13px] font-bold text-slate-900 truncate">{value}</p>
    </div>
  );
}
